import { useReducedMotion } from "framer-motion";
import { type CSSProperties, type PointerEvent, useCallback, useMemo, useRef } from "react";
import { MOTION, SHEET_STEP } from "../../../theme/tokens";

/** Pointer handlers for a sheet's drag zone (its grab handle, a step's heading), plus the style that zone needs. */
export type SheetDragHandleProps = {
	onPointerDown: (event: PointerEvent<HTMLElement>) => void;
	onPointerMove: (event: PointerEvent<HTMLElement>) => void;
	onPointerUp: () => void;
	onPointerCancel: () => void;
	onLostPointerCapture: () => void;
	style: CSSProperties;
};

/** A press on a control inside a drag zone is that control's, never a drag: a heading's close button, a search field. */
const NO_DRAG_SELECTOR = "button, input, textarea, select, a, [role='button'], [role='switch'], [role='checkbox'], [role='combobox']";

type Drag = { pointerId: number; startY: number; dy: number; paper: HTMLElement };

/**
 * Drag a bottom sheet down by its handle or heading to close it, without `SwipeableDrawer`.
 *
 * `SwipeableDrawer` owns every touch on the paper and cancels native scrolling inside it, so a sheet that scrolls (a unit
 * list, a staff search) could not be dragged. This drives the gesture only from the zones it is spread on: the paper
 * follows the finger down, a release past `SHEET_STEP.DRAG_CLOSE_THRESHOLD_PX` closes it (the drawer's own exit takes
 * over from where the finger left it), and anything shorter springs back. Everything else in the sheet scrolls and
 * taps as before.
 */
export const useSheetDragToClose = (onClose: () => void): SheetDragHandleProps => {
	const reduceMotion = useReducedMotion();
	const dragRef = useRef<Drag | null>(null);

	const onPointerDown = useCallback((event: PointerEvent<HTMLElement>): void => {
		if (event.button !== 0) return;
		if (event.target instanceof Element && event.target.closest(NO_DRAG_SELECTOR) !== null) return;
		const paper = event.currentTarget.closest(".MuiDrawer-paper");
		if (!(paper instanceof HTMLElement)) return;
		dragRef.current = { pointerId: event.pointerId, startY: event.clientY, dy: 0, paper };
		paper.style.transition = "none";
		event.currentTarget.setPointerCapture(event.pointerId);
	}, []);

	const onPointerMove = useCallback((event: PointerEvent<HTMLElement>): void => {
		const drag = dragRef.current;
		if (drag === null || drag.pointerId !== event.pointerId) return;
		// Down only: the sheet is already as tall as it gets, so an upward pull has nothing to reveal.
		drag.dy = Math.max(0, event.clientY - drag.startY);
		drag.paper.style.transform = `translateY(${drag.dy}px)`;
	}, []);

	const end = useCallback((): void => {
		const drag = dragRef.current;
		if (drag === null) return;
		dragRef.current = null;
		if (drag.dy > SHEET_STEP.DRAG_CLOSE_THRESHOLD_PX) {
			// The drawer's exit transition writes its own transform and transition, starting from where the finger let go.
			drag.paper.style.transition = "";
			onClose();
			return;
		}
		drag.paper.style.transition = reduceMotion ? "none" : `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`;
		drag.paper.style.transform = "";
	}, [onClose, reduceMotion]);

	return useMemo<SheetDragHandleProps>(
		() => ({
			onPointerDown,
			onPointerMove,
			onPointerUp: end,
			onPointerCancel: end,
			onLostPointerCapture: end,
			// The browser must not claim the drag as a scroll or a pull-to-refresh before the sheet sees it.
			style: { touchAction: "none" },
		}),
		[onPointerDown, onPointerMove, end],
	);
};
